import { useState, useEffect } from "react";
import DashboardLayout from "@/components/layout/DashboardLayout";
import apiClient from "@/utils/apiClient";
import { Button } from "@/components/ui/button";
import { 
  Phone, Video, Clock, AlertCircle, Heart, 
  Shield, Users, Sparkles, ExternalLink, MessageCircle
} from "lucide-react";

const LIVE_SERVICES = [
  {
    id: "crisis-intervention",
    title: "Crisis Intervention OnCALL",
    description: "Same-day maladaptive life stress response crisis or conflict intervention coaching",
    availability: "9:30 AM - 12:00 PM & 4:00 PM - 7:00 PM MDT",
    duration: "60 min",
    price: 185,
    type: "urgent",
    icon: AlertCircle
  },
  {
    id: "stress-management",
    title: "Live Stress Management Session",
    description: "Real-time guided stress management and adaptive response techniques",
    availability: "Daily sessions available",
    duration: "45 min",
    price: 95,
    type: "live",
    icon: Heart
  },
  {
    id: "group-therapy",
    title: "Group Mind-SPA Session",
    description: "Live group therapy using The Laughing Drama Therapist approach",
    availability: "Every Monday & Thursday 7:00 PM MDT",
    duration: "90 min",
    price: 49,
    type: "group",
    icon: Users
  }
];

const TYPE_STYLES = {
  urgent: { bg: 'bg-red-100', text: 'text-red-600', badge: 'bg-red-500' },
  live: { bg: 'bg-green-100', text: 'text-green-600', badge: 'bg-green-500' },
  group: { bg: 'bg-blue-100', text: 'text-blue-600', badge: 'bg-blue-500' }
};

export default function OnCallLive() {
  const [selectedService, setSelectedService] = useState(null);
  const [sessionLinks, setSessionLinks] = useState([]);

  useEffect(() => {
    const fetchLinks = async () => {
      try {
        const res = await apiClient.get("/session-links/active");
        setSessionLinks(res.data.links || []);
      } catch (e) { /* silent */ }
    };
    fetchLinks();
  }, []);

  return (
    <DashboardLayout>
      <div className="p-6 md:p-8 lg:p-12 space-y-8 animate-fade-in" data-testid="oncall-live-page">
        {/* Header */}
        <div className="relative">
          <div className="absolute -top-2 -left-2 w-4 h-4 bg-red-500 rounded-full animate-pulse"></div>
          <p className="overline-text mb-2 pl-6">LIVE NOW</p>
          <h1 className="font-serif text-3xl sm:text-4xl font-light text-spa-moss mb-3">
            The Laughing Drama Therapist OnCALL+ Live
          </h1>
          <p className="text-spa-text-secondary max-w-2xl">
            Stress Management Mind-SPA ICU - Real-time crisis intervention and stress management 
            sessions with Mr. RAPsoul.R, The Laughing Drama Therapist.
          </p>
        </div>

        {/* Live Status Banner */}
        <div className="spa-card bg-gradient-to-r from-red-500 to-red-600 text-white relative overflow-hidden">
          <div className="absolute top-4 right-4 flex items-center gap-2">
            <span className="w-3 h-3 bg-white rounded-full animate-pulse"></span>
            <span className="text-sm font-medium">LIVE</span>
          </div>
          <div className="flex flex-col md:flex-row items-start md:items-center gap-6">
            <div className="w-20 h-20 bg-white/20 rounded-2xl flex items-center justify-center">
              <Phone className="w-10 h-10" strokeWidth={1.5} />
            </div>
            <div className="flex-1">
              <h2 className="font-serif text-2xl mb-2">Mind-SPA ICU Available Now</h2>
              <p className="text-white/80 mb-4">
                Immediate support for acute stress, crisis situations, and urgent emotional wellness needs. 
                Trauma-informed, faith-centered approach aligned with WHO Mental Health Action Plan.
              </p>
              <div className="flex flex-wrap gap-4 text-sm">
                <span className="flex items-center gap-2">
                  <Clock className="w-4 h-4" /> Response within 15 minutes
                </span>
                <span className="flex items-center gap-2">
                  <Shield className="w-4 h-4" /> Confidential & Secure
                </span>
              </div>
            </div>
            <Button 
              className="bg-white text-red-600 hover:bg-white/90 rounded-full px-8"
              onClick={() => window.open('https://www.mind-spa.icu', '_blank')}
            >
              <Phone className="w-4 h-4 mr-2" /> Connect Now
            </Button>
          </div>
        </div>

        {/* Services Grid */}
        <div className="grid md:grid-cols-3 gap-6">
          {LIVE_SERVICES.map((service) => {
            const styles = TYPE_STYLES[service.type];
            return (
              <div 
                key={service.id}
                className={`spa-card cursor-pointer transition-all ${
                  selectedService?.id === service.id ? 'ring-2 ring-spa-terracotta' : ''
                }`}
                onClick={() => setSelectedService(service)}
                data-testid={`service-${service.id}`}
              >
                <div className="flex items-start justify-between mb-4">
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${styles.bg}`}>
                    <service.icon className={`w-6 h-6 ${styles.text}`} strokeWidth={1.5} />
                  </div>
                  <span className={`${styles.badge} text-white text-xs px-2 py-1 rounded-full uppercase`}>
                    {service.type}
                  </span>
                </div>
                <h3 className="font-serif text-lg text-spa-moss mb-2">{service.title}</h3>
                <p className="text-sm text-spa-text-secondary mb-4">{service.description}</p>
                <div className="space-y-2 text-sm">
                  <div className="flex items-center gap-2 text-spa-text-secondary">
                    <Clock className="w-4 h-4" />
                    <span>{service.duration}</span>
                  </div>
                  <p className="text-xs text-spa-text-secondary">{service.availability}</p>
                </div>
                <div className="mt-4 pt-4 border-t border-spa-border flex items-center justify-between">
                  <span className="font-serif text-xl text-spa-moss">CA${service.price}</span>
                  <Button className="spa-button-primary px-4 py-2 text-sm">
                    Book Now
                  </Button>
                </div>
              </div>
            );
          })}
        </div>

        {/* Features Section */}
        <div className="grid md:grid-cols-2 gap-6">
          <div className="spa-card">
            <div className="flex items-center gap-3 mb-4">
              <Sparkles className="w-6 h-6 text-spa-terracotta" />
              <h3 className="font-serif text-xl text-spa-moss">What to Expect</h3>
            </div>
            <ul className="space-y-3 text-spa-text-secondary">
              <li className="flex items-start gap-3">
                <span className="w-1.5 h-1.5 bg-spa-terracotta rounded-full mt-2"></span>
                <span>Immediate connection with Mr. RAPsoul.R or trained wellness facilitator</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="w-1.5 h-1.5 bg-spa-terracotta rounded-full mt-2"></span>
                <span>Trauma-informed, culturally sensitive support</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="w-1.5 h-1.5 bg-spa-terracotta rounded-full mt-2"></span>
                <span>Adaptive stress response techniques</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="w-1.5 h-1.5 bg-spa-terracotta rounded-full mt-2"></span>
                <span>Follow-up resources and community support</span>
              </li>
            </ul>
          </div>

          <div className="spa-card bg-spa-sage/10">
            <div className="flex items-center gap-3 mb-4">
              <Video className="w-6 h-6 text-spa-sage" />
              <h3 className="font-serif text-xl text-spa-moss">Connection Options</h3>
            </div>
            <div className="space-y-4">
              <a 
                href="https://www.mind-spa.icu"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-between p-4 bg-white rounded-xl hover:shadow-spa transition-shadow"
              >
                <span className="text-spa-moss font-medium">Mind-SPA ICU Portal</span>
                <ExternalLink className="w-4 h-4 text-spa-text-secondary" />
              </a>
              <a 
                href="https://www.shirvington.ca"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-between p-4 bg-white rounded-xl hover:shadow-spa transition-shadow"
              >
                <span className="text-spa-moss font-medium">SMAHoo Change Clinic</span>
                <ExternalLink className="w-4 h-4 text-spa-text-secondary" />
              </a>
              <div className="p-4 bg-white rounded-xl">
                <p className="text-spa-moss font-medium mb-1">Direct Line</p>
                <p className="text-spa-terracotta font-serif text-lg">403-803-4041</p>
              </div>
            </div>
          </div>
        </div>

        {/* Quick Connect: WhatsApp & Zoom */}
        {sessionLinks.length > 0 && (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="oncall-session-links">
            {sessionLinks.map(link => (
              <div key={link.link_id} className="spa-card flex items-center gap-4" data-testid={`oncall-link-${link.link_id}`}>
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${link.link_type === "zoom" ? "bg-blue-100" : "bg-green-100"}`}>
                  {link.link_type === "zoom" ? <Video className="w-6 h-6 text-blue-600" /> : <MessageCircle className="w-6 h-6 text-green-600" />}
                </div>
                <div className="flex-1 min-w-0">
                  <h4 className="font-medium text-spa-moss text-sm">{link.title}</h4>
                  <p className="text-xs text-spa-text-secondary truncate">{link.description}</p>
                </div>
                {link.link_type === "zoom" && link.url && (
                  <a href={link.url} target="_blank" rel="noopener noreferrer" className="px-3 py-1.5 bg-blue-600 text-white rounded-full text-xs hover:bg-blue-700 transition-colors flex items-center gap-1">
                    <Video className="w-3 h-3" /> Join
                  </a>
                )}
                {link.link_type === "whatsapp" && link.whatsapp_number && (
                  <a href={`https://wa.me/${link.whatsapp_number}${link.whatsapp_message ? `?text=${encodeURIComponent(link.whatsapp_message)}` : ""}`} target="_blank" rel="noopener noreferrer" className="px-3 py-1.5 bg-green-600 text-white rounded-full text-xs hover:bg-green-700 transition-colors flex items-center gap-1">
                    <MessageCircle className="w-3 h-3" /> Chat
                  </a>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Emergency Notice */}
        <div className="spa-card border-l-4 border-red-500 bg-red-50">
          <div className="flex items-start gap-4">
            <AlertCircle className="w-6 h-6 text-red-500 flex-shrink-0" />
            <div>
              <h4 className="font-medium text-spa-moss mb-1">Crisis Resources</h4>
              <p className="text-sm text-spa-text-secondary">
                If you are experiencing a life-threatening emergency, please call 911 or your local emergency services. 
                The Mind-SPA ICU is designed for emotional wellness support, not medical emergencies.
              </p>
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
